import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { Formik, Form, useField, useFormikContext } from 'formik'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { AsyncPaginate } from 'react-select-async-paginate'
import { array, string, number, object, mixed } from 'yup'
import {
  paymentType as paymentTypeConst,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import ExperienceButton from './ExperienceButton'
import {
  ExperienceInputField,
  ExperienceDatePicker,
  ExperienceCheckboxField,
  ExperienceSelect,
} from '../form'
import { experienceSelectStyles } from '../form/ExperienceSelect'
import {
  SetTotalSessionDuration,
  SetTotalSession,
} from '../coachingPlan/SetTotalSessionDuration'
import {
  buildSowEmailPreviewPayload,
  getConditionalSessionLabel,
  getCoachSchedulingFlags,
  getSowEmailPreviewDisabledReason,
} from '../coachingPlan/schedulingHelpers'
import {
  canBulkAssignIncludeBioInMail,
  getBulkBioInMailDisabledMessage,
  getBulkBioInMailPartialWarning,
} from '../scheduling/schedulingPreconditions'
import SOWEmailPreviewButton from '../coachingPlan/SOWEmailPreviewButton'
import SOWEmailPreviewModal from '../coachingPlan/SOWEmailPreviewModal'
import {
  canBulkAssignUsePlatformScheduling,
  getBulkPlatformSchedulingDisabledMessage,
} from '../scheduling/schedulingPreconditions'
import { buildBulkSOWData } from './utils/guidePairingUtils'
import {
  ConditionalSchedulingCheckbox,
  ExperienceQuestSelect,
  MAX_CONDITIONAL_SESSIONS,
  QUEST_SOW_TYPE,
  REGULAR_SOW_TYPE,
  SchedulingConditionsSection,
  SchedulingTypeRadios,
  SOWRow,
  SOWSchedulingEffects,
  SOWSection,
  SOWTypeRadios,
  isManualScheduling,
  questSOWDefaults,
} from './QuestSOWScheduling'
import Labels from '../../constants/labels'
import API from '../../utils/API'
import { getCoachingPlanObject } from '../../utils/utility'
import { isDateBefore, isValidDate } from '../../utils/date'

const { SOWBillEvery } = Labels
const { RECURRING, ONETIME } = paymentTypeConst

const emptyToUndefined = (value, originalValue) =>
  originalValue === '' || originalValue === null ? undefined : value

const isFiniteContract = (duration) =>
  duration != null && duration !== '' && duration !== 'Indefinite'

const amountTest = (label) =>
  function (value) {
    const { isPaymentRequired } = this.parent
    if (value === undefined || value === null) {
      return this.createError({ message: `${label} is required` })
    }
    if (isPaymentRequired === 'false' && value >= 0 && value <= 10000) {
      return true
    }
    if (value < 1) {
      return this.createError({
        message: `Please enter a ${label.toLowerCase()} of $1 or more`,
      })
    }
    if (value > 10000) {
      return this.createError({
        message: `The ${label.toLowerCase()} cannot exceed $10000`,
      })
    }
    return true
  }

// Same rules as the network bulk SOW form (IndividualSOWForm).
const sowSchema = object().shape({
  planName: string().trim().required('Plan name is required').max(160),
  paymentCycle: string().required(),
  invoiceEvery: mixed().when('paymentCycle', {
    is: RECURRING,
    then: number()
      .typeError('Bill every is required')
      .required('Bill every is required')
      .oneOf(SOWBillEvery),
    otherwise: mixed().notRequired(),
  }),
  numberOfBillingCycle: number()
    .transform(emptyToUndefined)
    .nullable()
    .when('paymentCycle', {
      is: RECURRING,
      then: (schema) =>
        schema.min(1, 'Minimum is 1').max(1000, 'Maximum is 1000').nullable(),
      otherwise: (schema) => schema.notRequired(),
    }),
  subscriptionAmount: number()
    .transform(emptyToUndefined)
    .nullable()
    .when('paymentCycle', {
      is: RECURRING,
      then: (schema) =>
        schema.test(
          'subscription-amount',
          'Invalid subscription amount',
          amountTest('Subscription amount'),
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  onetimeAmount: number()
    .transform(emptyToUndefined)
    .when('paymentCycle', {
      is: ONETIME,
      then: (schema) =>
        schema.test(
          'onetime-amount',
          'Invalid one-time amount',
          amountTest('One-time amount'),
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  upfrontFee: number()
    .transform(emptyToUndefined)
    .when('paymentCycle', {
      is: RECURRING,
      then: (schema) =>
        schema.required('Upfront fee is required').min(0).max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),
  totalSession: number()
    .transform(emptyToUndefined)
    .when(['numberOfBillingCycle', 'totalDurationOfContract'], {
      is: (billingCycle, contractDuration) =>
        (billingCycle != null && billingCycle !== '') ||
        isFiniteContract(contractDuration),
      then: (schema) =>
        schema
          .required('Total sessions is required')
          .min(1)
          .when('sowType', {
            is: QUEST_SOW_TYPE,
            then: (s) =>
              s.max(
                MAX_CONDITIONAL_SESSIONS,
                'A maximum of 3 sessions can be taken in the experience type SOW.',
              ),
            otherwise: (s) => s.max(999),
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
  sessionPerCoachingCycle: number()
    .typeError('Sessions per coaching cycle is required')
    .required('Sessions per coaching cycle is required')
    .min(1)
    .max(36),
  numberOfCoachingCycle: number()
    .typeError('Number of coaching cycles is required')
    .required('Number of coaching cycles is required')
    .oneOf(SOWBillEvery),
  sessionDuration: number()
    .typeError('Session duration is required')
    .required('Session duration is required')
    .min(15)
    .max(600),
  coachingRate: number()
    .typeError('Coaching rate is required')
    .required('Coaching rate is required')
    .min(0)
    .max(1000),
  growthCoachingRate: number()
    .transform(emptyToUndefined)
    .nullable()
    .when('includeGrowthSession', {
      is: true,
      then: (schema) =>
        schema.required('Growth coaching rate is required').min(0).max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),
  growthSessionDuration: number()
    .transform(emptyToUndefined)
    .nullable()
    .when('includeGrowthSession', {
      is: true,
      then: (schema) =>
        schema.required('Growth session duration is required').min(1).max(1000),
      otherwise: (schema) => schema.notRequired(),
    }),
  clientTimeZone: string().required('Timezone is required'),
  planStartDate: mixed()
    .required('Plan start date is required')
    .test('valid-date', 'Please enter valid date!', (value) =>
      moment(value, 'MM/DD/YYYY', true).isValid(),
    ),
  includeGrowthSession: mixed().notRequired(),
  sowType: string().required('SOW type is required'),
  questId: string().when(['sowType', 'isPlatformScheduling'], {
    is: (sowType, isPlatformScheduling) =>
      sowType === QUEST_SOW_TYPE && !isManualScheduling(isPlatformScheduling),
    then: (schema) => schema.required('Experience is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isPlatformScheduling: mixed().notRequired(),
  isConditionalSchedulingEnabled: mixed().notRequired(),
  schedulingConditions: array().when(
    ['isConditionalSchedulingEnabled', 'totalSession'],
    {
      is: (isEnabled, totalSession) =>
        !!isEnabled &&
        Number(totalSession) > 0 &&
        Number(totalSession) <= MAX_CONDITIONAL_SESSIONS,
      then: (schema) =>
        schema
          .test(
            'scheduling-conditions-length',
            'All sessions must have scheduling conditions',
            function (value) {
              const total = Number(this.parent.totalSession) || 0
              if (!Array.isArray(value) || value.length !== total) {
                return this.createError({
                  message: `Please configure scheduling conditions for all ${total} session(s)`,
                })
              }
              return true
            },
          )
          .of(
            object().shape({
              sessionIndex: number().required(),
              condition: string()
                .required('Condition is required')
                .oneOf(
                  ['on_or_before', 'on_or_after', 'between'],
                  'Invalid condition',
                ),
              primaryDate: string()
                .required('Start date is required')
                .test('valid-date', 'Please enter valid start date', (value) =>
                  isValidDate(value, 'MM/DD/YYYY'),
                ),
              secondaryDate: string().when('condition', {
                is: 'between',
                then: (schema) =>
                  schema
                    .required('End date is required')
                    .test(
                      'valid-end-date',
                      'Please enter valid end date',
                      function (value) {
                        if (!isValidDate(value, 'MM/DD/YYYY')) return false
                        const { primaryDate } = this.parent
                        if (
                          isValidDate(primaryDate, 'MM/DD/YYYY') &&
                          isDateBefore(value, primaryDate, 'MM/DD/YYYY')
                        ) {
                          return this.createError({
                            message: 'End date must be after start date',
                          })
                        }
                        return true
                      },
                    ),
                otherwise: (schema) => schema.notRequired(),
              }),
            }),
          ),
      otherwise: (schema) => schema.notRequired(),
    },
  ),
})

const baseSOWValues = {
  planName: '',
  planStartDate: null,
  clientTimeZone: '',
  isPaymentRequired: 'false',
  paymentCycle: ONETIME,
  onetimeAmount: '',
  invoiceEvery: '',
  numberOfBillingCycle: '',
  subscriptionAmount: '',
  upfrontFee: '',
  totalDurationOfContract: '',
  sessionPerCoachingCycle: '',
  numberOfCoachingCycle: '',
  typeOfCoachingCycle: 'Monthly',
  sessionDuration: '',
  totalSession: '',
  sugTotalSession: '',
  coachingRate: '',
  coachingRateType: 'hourly',
  includeGrowthSession: false,
  growthSessionDuration: '',
  growthCoachingRate: '',
  growthCoachingRateType: 'hourly',
  sowCopy: null,
  sowType: REGULAR_SOW_TYPE,
  questId: '',
  isPlatformScheduling: 'true',
  isConditionalSchedulingEnabled: false,
  schedulingConditions: [],
  includeBioInMail: false,
}

// Experience SOW by default (linked to this experience), like the network
// flow for quest-created networks. Falls back to REGULAR + manual scheduling
// when a paired guide can't use platform scheduling.
export const getInitialSOWValues = ({
  questId = '',
  canUsePlatformScheduling = true,
} = {}) => {
  if (!canUsePlatformScheduling) {
    return { ...baseSOWValues, isPlatformScheduling: 'false' }
  }
  return {
    ...baseSOWValues,
    ...questSOWDefaults,
    sowType: QUEST_SOW_TYPE,
    questId,
  }
}

const cycleUnit = (type) =>
  type === 'Weekly' ? 'week(s)' : type === 'Yearly' ? 'year(s)' : 'month(s)'

const rateUnit = (type) => (type === 'hourly' ? 'hour' : 'session')

const timeZoneOptions = Labels.timeZoneList.map((tz) => ({
  value: tz.value,
  label: `${tz.label} - ${tz.value}`,
}))

// Mirrors the regular session duration/rate into the growth fields once, for
// each field still empty: on enabling growth, or shortly after the regular value
// is typed (debounced so a half-typed number isn't copied).
// Turns the bio flag on when a paired coach has a bio, and off when none does (same rule as the SOW form)
const IncludeBioInMailDefault = ({ isAvailable, sowType, coachKey }) => {
  const { setFieldValue } = useFormikContext()

  useEffect(() => {
    setFieldValue('includeBioInMail', isAvailable)
  }, [isAvailable, sowType, coachKey, setFieldValue])

  return null
}

const GrowthSessionPrefill = ({ values, setFieldValue }) => {
  const { includeGrowthSession, sessionDuration, coachingRate } = values
  const filledRef = useRef({ duration: false, rate: false })
  const isEmpty = (v) => v === '' || v === null || v === undefined

  useEffect(() => {
    if (!includeGrowthSession) {
      filledRef.current = { duration: false, rate: false }
      return
    }
    setFieldValue('growthCoachingRateType', values.coachingRateType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeGrowthSession])

  useEffect(() => {
    if (
      !includeGrowthSession ||
      filledRef.current.duration ||
      isEmpty(sessionDuration) ||
      !isEmpty(values.growthSessionDuration)
    ) {
      return undefined
    }
    const timer = setTimeout(() => {
      filledRef.current.duration = true
      setFieldValue('growthSessionDuration', sessionDuration)
    }, 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeGrowthSession, sessionDuration, values.growthSessionDuration])

  useEffect(() => {
    if (
      !includeGrowthSession ||
      filledRef.current.rate ||
      isEmpty(coachingRate) ||
      !isEmpty(values.growthCoachingRate)
    ) {
      return undefined
    }
    const timer = setTimeout(() => {
      filledRef.current.rate = true
      setFieldValue('growthCoachingRate', coachingRate)
    }, 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeGrowthSession, coachingRate, values.growthCoachingRate])

  return null
}

const paymentCycleOptions = [
  { value: ONETIME, label: 'One Time Payment' },
  { value: RECURRING, label: 'Recurring Payment' },
]

const billEveryOptions = SOWBillEvery.map((n) => ({
  value: n,
  label: String(n),
}))

const coachingCycleTypeOptions = [
  { value: 'Weekly', label: 'Week' },
  { value: 'Monthly', label: 'Month' },
  { value: 'Yearly', label: 'Year' },
]

const rateTypeOptions = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'session', label: 'Session' },
]

const paymentRequiredOptions = [
  {
    value: 'true',
    label: 'YES',
    description: '(The link will be sent to the client)',
  },
  {
    value: 'false',
    label: 'NO',
    description:
      '(You can still add payment if needed for internal use/Reference)',
  },
]

const coachRateTooltip = (
  <Tooltip id='quest-sow-rate-tooltip' className='experience-sow-tooltip'>
    - Tier 1 - $375 / hour (Corporation paying for Executive)
    <br />- Tier 2 - $250 / hour (Senior Exec Self-Paying)
    <br />- Tier 3 - $175 / hour (Emerging Leader / Manager / High potential)
    <br />- Custom - Social impact discounts or custom fees as negotiated with
    client (should be 50% of top line hourly rate)
  </Tooltip>
)

const RateLabel = ({ children }) => (
  <>
    {children}
    <OverlayTrigger placement='top' overlay={coachRateTooltip}>
      <span className='experience-sow-info' aria-label='Coach rate tiers'>
        i
      </span>
    </OverlayTrigger>
  </>
)

const SOWSplit = ({ secondLabel, secondHtmlFor, first, second }) => (
  <div className='experience-sow-split'>
    {first}
    <label className='experience-f-label' htmlFor={secondHtmlFor}>
      {secondLabel}
    </label>
    {second}
  </div>
)

// Two fields side by side, each with its label above
const PaymentRequiredRadios = ({ disabled }) => {
  const [field, , helpers] = useField('isPaymentRequired')
  return (
    <div className='experience-sow-radios' role='radiogroup'>
      {paymentRequiredOptions.map((opt) => (
        <label className='experience-sow-radio' key={opt.value}>
          <input
            type='radio'
            name={field.name}
            value={opt.value}
            checked={field.value === opt.value}
            disabled={disabled}
            onChange={() => helpers.setValue(opt.value)}
          />
          <b>{opt.label}</b>
          <span>{opt.description}</span>
        </label>
      ))}
    </div>
  )
}

const cycleLabelMap = {
  Weekly: 'week(s)',
  Monthly: 'month(s)',
  Yearly: 'year(s)',
}

const getPlanLabel = (plan) =>
  `${plan?.planName} ${
    plan?.clientId?.fullName ? `(${plan?.clientId?.fullName})` : ''
  } - ${plan?.sessionPerCoachingCycle} session(s)/${
    plan?.numberOfCoachingCycle
  } ${cycleLabelMap[plan?.typeOfCoachingCycle] || ''}`

const SOWCopyFrom = ({ disabled, canUsePlatformScheduling }) => {
  const { values, setValues } = useFormikContext()
  const ErrorService = useErrorService()

  const loadOptions = async (search) => {
    try {
      const { data } = await API.AdminAPI.coachPlan.getCoachingPlanList(search)
      return {
        options: (data || []).map((plan) => ({
          value: plan._id,
          label: getPlanLabel(plan),
          extra: plan,
        })),
        hasMore: false,
      }
    } catch (e) {
      ErrorService.showError(e)
      return { options: [], hasMore: false }
    }
  }

  const handleChange = (option) => {
    if (!option) {
      setValues({ ...values, sowCopy: null })
      return
    }
    const copied = getCoachingPlanObject(
      { sow: option.extra },
      Labels.coachingPlanStatus[0].value,
    )
    const next = { ...values, sowCopy: option }
    Object.keys(values).forEach((key) => {
      if (key in copied) next[key] = copied[key] ?? ''
    })
    next.isPlatformScheduling =
      canUsePlatformScheduling &&
      !isManualScheduling(copied.isPlatformScheduling)
        ? 'true'
        : 'false'
    // Keep the experience this SOW is being created for, not the copied one.
    next.questId = next.sowType === QUEST_SOW_TYPE ? values.questId : ''
    if (!Array.isArray(next.schedulingConditions))
      next.schedulingConditions = []
    setValues(next, true)
  }

  return (
    <div className='experience-f'>
      <AsyncPaginate
        inputId='quest-sow-sowCopy'
        classNamePrefix='experience-select'
        className='experience-select-control'
        value={values.sowCopy || null}
        loadOptions={loadOptions}
        debounceTimeout={300}
        onChange={handleChange}
        isClearable
        isDisabled={disabled}
        placeholder='Select SOW to copy'
        styles={experienceSelectStyles}
        menuPosition='fixed'
        menuPortalTarget={document.body}
      />
    </div>
  )
}

const conditionLabels = {
  on_or_before: 'On or before',
  on_or_after: 'On or after',
  between: 'Between',
}

const confirmRows = (values, pairCount, questLabel) => {
  const isRecurring = values.paymentCycle === RECURRING
  const isQuest = values.sowType === QUEST_SOW_TYPE
  const rows = [
    [
      'Scheduling type',
      isManualScheduling(values.isPlatformScheduling) ? 'Manual' : 'Platform',
    ],
    ['SOW type', isQuest ? 'Experience' : 'Regular'],
  ]
  if (isQuest) rows.push(['Experience', questLabel || '—'])
  rows.push(
    ['Plan name', values.planName],
    ['Pairs', String(pairCount)],
    ['Plan start date', values.planStartDate || '—'],
    ['Timezone', values.clientTimeZone || '—'],
    ['Payment required', values.isPaymentRequired === 'true' ? 'Yes' : 'No'],
    ['Payment cycle', isRecurring ? 'Recurring' : 'One-time'],
  )
  if (isRecurring) {
    rows.push(
      ['Bill every', `${values.invoiceEvery} month(s)`],
      ['Subscription amount (USD)', values.subscriptionAmount],
      ['Upfront / setup fee (USD)', values.upfrontFee],
      ['Billing cycles', values.numberOfBillingCycle || 'Indefinite'],
    )
  } else {
    rows.push(['One-time amount (USD)', values.onetimeAmount])
  }
  if (!isRecurring || (values.invoiceEvery && values.numberOfBillingCycle)) {
    rows.push([
      'Total duration of contract',
      values.totalDurationOfContract
        ? `${values.totalDurationOfContract} month(s)`
        : 'Indefinite',
    ])
  }
  rows.push(
    [
      'Sessions',
      `${values.sessionPerCoachingCycle} per ${
        values.numberOfCoachingCycle
      } ${cycleUnit(values.typeOfCoachingCycle)}`,
    ],
    ['Session duration', `${values.sessionDuration} minutes`],
  )
  if (values.totalSession) rows.push(['Total sessions', values.totalSession])
  rows.push([
    'Coaching rate (USD)',
    `${values.coachingRate} / ${rateUnit(values.coachingRateType)}`,
  ])
  if (values.includeGrowthSession) {
    rows.push(
      ['Growth session duration', `${values.growthSessionDuration} minutes`],
      [
        'Growth coaching rate (USD)',
        `${values.growthCoachingRate} / ${rateUnit(
          values.growthCoachingRateType,
        )}`,
      ],
    )
  }
  if (!isManualScheduling(values.isPlatformScheduling)) {
    const conditions = values.isConditionalSchedulingEnabled
      ? values.schedulingConditions || []
      : []
    rows.push([
      'Conditional scheduling',
      conditions.length ? 'Enabled' : 'Disabled',
    ])
    conditions.forEach((item, index) => {
      const range =
        item.condition === 'between'
          ? `${item.primaryDate} – ${item.secondaryDate}`
          : item.primaryDate
      rows.push([
        getConditionalSessionLabel(index + 1, values.sowType),
        `${conditionLabels[item.condition] || '—'} ${range || ''}`.trim(),
      ])
    })
  }
  return rows
}

const getBulkFailure = (status) =>
  status && typeof status === 'object' && status.success === false
    ? status.message || 'Failed to assign SOW. Please try again.'
    : ''

// "<intro>: A, B. <advice>" -> intro, one name per line, advice. Anything else
// is shown as plain text.
const BulkErrorMessage = ({ message }) => {
  const match = message.match(/^([^:]+:)\s*(.+?)\.\s+(Please[\s\S]*)$/)
  if (!match) {
    return <p className='experience-f-hint experience-gp-lead'>{message}</p>
  }
  const [, intro, names, advice] = match
  return (
    <div className='experience-gp-lead'>
      <p className='experience-f-hint mb-2'>{intro}</p>
      <ul className='experience-gp-error-list'>
        {names.split(/,\s*/).map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
      <p className='experience-f-hint mb-0'>{advice}</p>
    </div>
  )
}

const QuestAssignSOWModal = ({
  show,
  pairs,
  guides,
  schedulingMeta,
  currentQuest,
  initialValues,
  onBackToPairing,
  onCancel,
  onAssigned,
}) => {
  const [step, setStep] = useState('form')
  const [confirmValues, setConfirmValues] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showEmailPreview, setShowEmailPreview] = useState(false)
  const ErrorService = useErrorService()
  const Notification = useNotificationService()
  const [questOptions, setQuestOptions] = useState([])

  const coachIds = useMemo(
    () => Array.from(new Set(pairs.map((pair) => pair.coachId))),
    [pairs],
  )
  const coachFlags = schedulingMeta?.coachSchedulingFlagsByCoachId || {}
  const canUsePlatformScheduling =
    !schedulingMeta?.forceManualScheduling &&
    canBulkAssignUsePlatformScheduling(coachIds, coachFlags)
  const platformUnavailableMessage = canUsePlatformScheduling
    ? ''
    : getBulkPlatformSchedulingDisabledMessage(coachIds, guides, coachFlags)

  useEffect(() => {
    if (show) {
      setStep('form')
      setConfirmValues(null)
    }
  }, [show])

  if (!show) return null

  const assignSOW = async (values) => {
    if (pairs.length === 0) {
      ErrorService.showError(new Error('No coach selected'))
      return
    }
    setIsSubmitting(true)
    try {
      const response = await API.AdminAPI.common.clientCoachBulkPairing({
        sowData: buildBulkSOWData(values),
        clientCoachMappingData: pairs,
      })
      // Business failures (e.g. clients not registered) come back as
      // { status: { success: false, message } }.
      const failure = getBulkFailure(response?.data?.status || response?.status)
      if (failure) {
        setErrorMessage(failure)
        return
      }
      if (response?.status) {
        Notification.showNotification(
          `SOW assigned to ${pairs.length} pair${
            pairs.length === 1 ? '' : 's'
          }.`,
          'success',
        )
        onAssigned()
      }
    } catch (e) {
      const failure = getBulkFailure(
        e?.response?.data?.status || e?.data?.status || e?.status,
      )
      if (failure) {
        setErrorMessage(failure)
      } else {
        ErrorService.showError(e)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const pairLabel = `${pairs.length} pair${pairs.length === 1 ? '' : 's'}`
  const getQuestLabel = (id) =>
    questOptions.find((option) => option.value === id)?.label ||
    (currentQuest?.value === id ? currentQuest?.label : '')

  return (
    <div
      className='experience-gp-modal-backdrop'
      role='dialog'
      aria-modal='true'
      aria-labelledby='gpSowTitle'>
      <Formik
        initialValues={initialValues}
        validationSchema={sowSchema}
        onSubmit={async (values) => {
          if (values.isPaymentRequired === 'true') {
            setConfirmValues(values)
            setStep('confirm')
            return
          }
          await assignSOW(values)
        }}>
        {({ values, setFieldValue, submitCount, isValid, submitForm }) => {
          const showErrors = submitCount > 0
          const isRecurring = values.paymentCycle === RECURRING
          const showContract =
            values.paymentCycle === ONETIME ||
            (isRecurring && values.invoiceEvery && values.numberOfBillingCycle)
          const showTotalSession =
            (values.numberOfBillingCycle !== '' &&
              isFiniteContract(values.totalDurationOfContract)) ||
            (values.paymentCycle === ONETIME &&
              isFiniteContract(values.totalDurationOfContract))
          const fieldProps = (name) => ({
            name,
            id: `quest-sow-${name}`,
            showErrorMessage: showErrors,
            disabled: isSubmitting,
          })

          // The preview is for the first pair, with the same bio flag the save sends
          const firstPair = pairs[0]
          const isPlatform = !isManualScheduling(values.isPlatformScheduling)
          const isBioAvailable =
            isPlatform &&
            canBulkAssignIncludeBioInMail(coachIds, coachFlags, values.sowType)
          const bioDisabledMessage = isPlatform
            ? getBulkBioInMailDisabledMessage(
                coachIds,
                guides,
                coachFlags,
                values.sowType,
              )
            : 'Including the bio in the schedule email is only available with Platform Scheduling.'
          const bioPartialWarning = isBioAvailable
            ? getBulkBioInMailPartialWarning(
                coachIds,
                guides,
                coachFlags,
                values.sowType,
              )
            : ''
          const emailPreviewDisabledReason = getSowEmailPreviewDisabledReason({
            values,
            clientId: firstPair?.clientId,
            coachId: firstPair?.coachId,
          })
          const emailPreviewPayload = emailPreviewDisabledReason
            ? null
            : buildSowEmailPreviewPayload({
                values,
                clientId: firstPair.clientId,
                coachId: firstPair.coachId,
              })
          const emailPreviewCoachFlags = getCoachSchedulingFlags(
            guides,
            firstPair?.coachId,
            schedulingMeta?.coachSchedulingFlagsByCoachId,
          )
          const isCoachMissingVideoCallLink =
            values.sowType !== QUEST_SOW_TYPE &&
            emailPreviewCoachFlags.hasVideoCallLink === false

          return (
            <div className='experience-gp-sow-card'>
              <SetTotalSessionDuration />
              <SetTotalSession />
              <GrowthSessionPrefill
                values={values}
                setFieldValue={setFieldValue}
              />
              <SOWSchedulingEffects
                values={values}
                setFieldValue={setFieldValue}
                notificationService={Notification}
                canUsePlatformScheduling={canUsePlatformScheduling}
                defaultQuestId={currentQuest?.value}
              />
              <div className='experience-gp-pair-head'>
                <h2 id='gpSowTitle'>
                  {step === 'confirm' ? 'Confirm SOW' : 'Assign SOW'}
                </h2>
                <button
                  type='button'
                  className='experience-gp-x'
                  aria-label='Close'
                  disabled={isSubmitting}
                  onClick={onCancel}>
                  ×
                </button>
              </div>
              <p className='experience-f-hint experience-gp-lead'>
                {step === 'confirm'
                  ? `Once saved, the plan can only be edited for timezone and status. It will be applied to ${pairLabel}.`
                  : `One SOW will be applied to ${pairLabel} from the previous step.`}
              </p>
              {showErrors && !isValid && step === 'form' ? (
                <p className='experience-f-error experience-gp-form-error'>
                  Please fix the highlighted fields.
                </p>
              ) : null}

              <div className='experience-gp-pair-body'>
                {step === 'form' ? (
                  <Form id='questAssignSowForm'>
                    <SOWSection title='SOW Details'>
                      <SOWRow label='Scheduling Type'>
                        <SchedulingTypeRadios
                          disabled={isSubmitting}
                          canUsePlatformScheduling={canUsePlatformScheduling}
                          unavailableMessage={platformUnavailableMessage}
                        />
                      </SOWRow>
                      <SOWRow label='Copy From' htmlFor='quest-sow-sowCopy'>
                        <SOWCopyFrom
                          disabled={isSubmitting}
                          canUsePlatformScheduling={canUsePlatformScheduling}
                        />
                      </SOWRow>
                      <SOWRow label='Plan Name' htmlFor='quest-sow-planName'>
                        <ExperienceInputField
                          {...fieldProps('planName')}
                          placeholder='Plan Name'
                        />
                      </SOWRow>
                      <SOWRow label='SOW Type'>
                        <SOWTypeRadios disabled={isSubmitting} />
                      </SOWRow>
                      {values.sowType === QUEST_SOW_TYPE ? (
                        <SOWRow label='Experience' htmlFor='quest-sow-questId'>
                          <ExperienceQuestSelect
                            disabled={isSubmitting}
                            showErrorMessage={showErrors}
                            currentOption={currentQuest}
                            cachedOptions={questOptions}
                            onOptionsLoaded={setQuestOptions}
                          />
                        </SOWRow>
                      ) : null}
                      <SOWRow>
                        <IncludeBioInMailDefault
                          isAvailable={isBioAvailable}
                          sowType={values.sowType}
                          coachKey={coachIds.join(',')}
                        />
                        <div className='experience-sow-checks'>
                          <ExperienceCheckboxField
                            name='includeBioInMail'
                            label='Include Bio in the Schedule Email?'
                            disabled={isSubmitting || !isBioAvailable}
                          />
                        </div>
                        {!isBioAvailable && bioDisabledMessage ? (
                          <p className='experience-f-hint'>
                            {bioDisabledMessage}
                          </p>
                        ) : null}
                        {bioPartialWarning ? (
                          <p className='experience-f-hint'>
                            {bioPartialWarning}
                          </p>
                        ) : null}
                      </SOWRow>
                    </SOWSection>

                    <SOWSection title='Billing'>
                      <SOWRow label='Payment Required?'>
                        <PaymentRequiredRadios disabled={isSubmitting} />
                      </SOWRow>
                      {isRecurring ? (
                        <SOWRow
                          label='Payment Type'
                          htmlFor='quest-sow-paymentCycle'>
                          <ExperienceSelect
                            {...fieldProps('paymentCycle')}
                            placeholder='Select Payment Type'
                            options={paymentCycleOptions}
                            isSearchable={false}
                          />
                        </SOWRow>
                      ) : (
                        <>
                          <SOWRow
                            label='Payment Type'
                            htmlFor='quest-sow-paymentCycle'>
                            <ExperienceSelect
                              {...fieldProps('paymentCycle')}
                              placeholder='Select Payment Type'
                              options={paymentCycleOptions}
                              isSearchable={false}
                            />
                          </SOWRow>
                          <SOWRow
                            label='One Time Fee (USD)'
                            htmlFor='quest-sow-onetimeAmount'>
                            <ExperienceInputField
                              {...fieldProps('onetimeAmount')}
                              type='number'
                              placeholder='Enter One Time Fee'
                            />
                          </SOWRow>
                        </>
                      )}
                      {isRecurring ? (
                        <>
                          <SOWRow
                            label='Bill Every (Month(s))'
                            htmlFor='quest-sow-invoiceEvery'>
                            <ExperienceSelect
                              {...fieldProps('invoiceEvery')}
                              placeholder='Select Month(s)'
                              options={billEveryOptions}
                              isSearchable={false}
                            />
                          </SOWRow>
                          <SOWRow
                            label='Number of Billing Cycle'
                            htmlFor='quest-sow-numberOfBillingCycle'>
                            <ExperienceInputField
                              {...fieldProps('numberOfBillingCycle')}
                              type='number'
                              placeholder='Indefinite'
                            />
                          </SOWRow>
                          <SOWRow
                            label='Subscription Amount (USD)'
                            htmlFor='quest-sow-subscriptionAmount'>
                            <ExperienceInputField
                              {...fieldProps('subscriptionAmount')}
                              type='number'
                              placeholder='Enter Subscription Fee'
                            />
                          </SOWRow>
                          <SOWRow
                            label='Upfront/Setup Fee (USD)'
                            htmlFor='quest-sow-upfrontFee'>
                            <ExperienceInputField
                              {...fieldProps('upfrontFee')}
                              type='number'
                              placeholder='Enter Upfront Fee'
                            />
                          </SOWRow>
                        </>
                      ) : null}
                      {showContract ? (
                        <SOWRow
                          label='Total Duration Of Contract'
                          htmlFor='quest-sow-totalDurationOfContract'>
                          <ExperienceInputField
                            {...fieldProps('totalDurationOfContract')}
                            type={
                              values.paymentCycle === ONETIME
                                ? 'number'
                                : 'text'
                            }
                            placeholder='Indefinite'
                            disabled={isSubmitting || isRecurring}
                          />
                        </SOWRow>
                      ) : null}
                    </SOWSection>

                    <SOWSection title='Coaching'>
                      <SOWRow
                        label='Number of Sessions'
                        htmlFor='quest-sow-sessionPerCoachingCycle'>
                        <div className='experience-sow-sessions'>
                          <ExperienceInputField
                            {...fieldProps('sessionPerCoachingCycle')}
                            type='number'
                            placeholder='Number of sessions'
                          />
                          <span className='experience-sow-per'>Per</span>
                          <ExperienceSelect
                            {...fieldProps('numberOfCoachingCycle')}
                            placeholder={`Number of ${cycleUnit(
                              values.typeOfCoachingCycle,
                            ).replace('(s)', 's')}`}
                            options={billEveryOptions}
                            isSearchable={false}
                          />
                          <ExperienceSelect
                            {...fieldProps('typeOfCoachingCycle')}
                            options={coachingCycleTypeOptions}
                            isSearchable={false}
                          />
                        </div>
                      </SOWRow>
                      {showTotalSession ? (
                        <SOWRow
                          label='Total Session'
                          htmlFor='quest-sow-totalSession'>
                          <div className='experience-sow-total'>
                            <ExperienceInputField
                              {...fieldProps('totalSession')}
                              showErrorMessage={
                                showErrors ||
                                (values.sowType === QUEST_SOW_TYPE &&
                                  Number(values.totalSession) >
                                    MAX_CONDITIONAL_SESSIONS)
                              }
                              type='number'
                              placeholder='Total session'
                            />
                            <span className='experience-sow-suggested'>
                              Suggested Total Sessions:{' '}
                              <b>{values.sugTotalSession || '-'}</b>
                            </span>
                          </div>
                        </SOWRow>
                      ) : null}
                      <SOWRow>
                        <div className='experience-sow-checks'>
                          <ExperienceCheckboxField
                            name='includeGrowthSession'
                            label='Includes Growth Plan?'
                            disabled={isSubmitting}
                          />
                          <ConditionalSchedulingCheckbox
                            disabled={isSubmitting}
                          />
                        </div>
                      </SOWRow>
                      <SOWRow
                        label='Regular Session Duration'
                        htmlFor='quest-sow-sessionDuration'>
                        {values.includeGrowthSession ? (
                          <SOWSplit
                            secondLabel='Growth Session Duration'
                            secondHtmlFor='quest-sow-growthSessionDuration'
                            first={
                              <ExperienceInputField
                                {...fieldProps('sessionDuration')}
                                type='number'
                                placeholder='Duration In Minutes'
                              />
                            }
                            second={
                              <ExperienceInputField
                                {...fieldProps('growthSessionDuration')}
                                type='number'
                                placeholder='Duration In Minutes'
                                list='quest-sow-duration-options'
                              />
                            }
                          />
                        ) : (
                          <ExperienceInputField
                            {...fieldProps('sessionDuration')}
                            type='number'
                            placeholder='Duration In Minutes'
                          />
                        )}
                      </SOWRow>
                      <SOWRow
                        label={
                          <RateLabel>
                            Regular Session Coach Rate (USD)
                          </RateLabel>
                        }
                        htmlFor='quest-sow-coachingRate'>
                        {values.includeGrowthSession ? (
                          <SOWSplit
                            secondLabel='Growth Session Coach Rate (USD)'
                            secondHtmlFor='quest-sow-growthCoachingRate'
                            first={
                              <ExperienceInputField
                                {...fieldProps('coachingRate')}
                                type='number'
                                placeholder='Rate'
                                list='quest-sow-hourly-rate'
                              />
                            }
                            second={
                              <ExperienceInputField
                                {...fieldProps('growthCoachingRate')}
                                type='number'
                                placeholder='Rate'
                                list='quest-sow-hourly-rate'
                              />
                            }
                          />
                        ) : (
                          <ExperienceInputField
                            {...fieldProps('coachingRate')}
                            type='number'
                            placeholder='Rate'
                            list='quest-sow-hourly-rate'
                          />
                        )}
                      </SOWRow>
                      <SOWRow
                        label='Regular Session Rate Type'
                        htmlFor='quest-sow-coachingRateType'>
                        {values.includeGrowthSession ? (
                          <SOWSplit
                            secondLabel='Growth Session Rate Type'
                            secondHtmlFor='quest-sow-growthCoachingRateType'
                            first={
                              <ExperienceSelect
                                {...fieldProps('coachingRateType')}
                                placeholder='Select Rate Type'
                                options={rateTypeOptions}
                                isSearchable={false}
                              />
                            }
                            second={
                              <ExperienceSelect
                                {...fieldProps('growthCoachingRateType')}
                                placeholder='Select Rate Type'
                                options={rateTypeOptions}
                                isSearchable={false}
                              />
                            }
                          />
                        ) : (
                          <ExperienceSelect
                            {...fieldProps('coachingRateType')}
                            placeholder='Select Rate Type'
                            options={rateTypeOptions}
                            isSearchable={false}
                          />
                        )}
                      </SOWRow>
                      <datalist id='quest-sow-hourly-rate'>
                        <option value='175'>
                          $175 - Emerging Leader (Tier 3)
                        </option>
                        <option value='250'>
                          $250 - Executive Leader (Tier 2)
                        </option>
                        <option value='375'>
                          $375 - Senior/Corporate Executive Leader (Tier 1)
                        </option>
                      </datalist>
                      <datalist id='quest-sow-duration-options'>
                        <option value='45'>45 Minutes Session Duration</option>
                        <option value='60'>60 Minutes Session Duration</option>
                      </datalist>
                      <SOWRow
                        label='Client Time Zone'
                        htmlFor='quest-sow-clientTimeZone'>
                        <ExperienceSelect
                          {...fieldProps('clientTimeZone')}
                          placeholder='Select Time Zone'
                          options={timeZoneOptions}
                        />
                      </SOWRow>
                      <SOWRow label='Plan Starts On'>
                        <div className='experience-f'>
                          <ExperienceDatePicker
                            name='planStartDate'
                            showErrorMessage={showErrors}
                            disabled={isSubmitting}
                            className='experience-f-control'
                            position='up'
                            minimumDate={moment().subtract(1, 'years')}
                            maximumDate={moment().add(1, 'years')}
                          />
                        </div>
                      </SOWRow>
                    </SOWSection>

                    {!isManualScheduling(values.isPlatformScheduling) ? (
                      <SOWSection title='Scheduling'>
                        <SchedulingConditionsSection
                          disabled={isSubmitting}
                          showErrors={showErrors}
                        />
                      </SOWSection>
                    ) : null}
                  </Form>
                ) : (
                  <div className='experience-gp-sow-confirm'>
                    {confirmRows(
                      confirmValues || values,
                      pairs.length,
                      getQuestLabel((confirmValues || values).questId),
                    ).map(([label, value]) => (
                      <div className='experience-status-line' key={label}>
                        <span>{label}</span>
                        <b>{value}</b>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <SOWEmailPreviewModal
                show={showEmailPreview}
                onHide={() => setShowEmailPreview(false)}
                payload={emailPreviewPayload}
                isPaymentRequired={values.isPaymentRequired === 'true'}
                isCoachMissingVideoCallLink={isCoachMissingVideoCallLink}
              />

              {step === 'form' ? (
                <div className='experience-gp-modal-actions'>
                  <ExperienceButton
                    variant='outline'
                    disabled={isSubmitting}
                    onClick={() => onBackToPairing(values)}>
                    Back to Pairing
                  </ExperienceButton>
                  <ExperienceButton
                    variant='outline'
                    disabled={isSubmitting}
                    onClick={onCancel}>
                    Cancel
                  </ExperienceButton>
                  <SOWEmailPreviewButton
                    disabledReason={emailPreviewDisabledReason}
                    onClick={() => setShowEmailPreview(true)}
                    ButtonComponent={ExperienceButton}
                    buttonProps={{ variant: 'outline' }}
                    wrapperClassName='d-inline-block'
                  />
                  <ExperienceButton
                    variant='primary'
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={submitForm}>
                    Save
                  </ExperienceButton>
                </div>
              ) : (
                <div className='experience-gp-modal-actions'>
                  <ExperienceButton
                    variant='outline'
                    disabled={isSubmitting}
                    onClick={() => setStep('form')}>
                    Back
                  </ExperienceButton>
                  <ExperienceButton
                    variant='outline'
                    disabled={isSubmitting}
                    onClick={() =>
                      assignSOW({ ...confirmValues, isReadyForPayment: false })
                    }>
                    Save as draft
                  </ExperienceButton>
                  <ExperienceButton
                    variant='primary'
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={() =>
                      assignSOW({ ...confirmValues, isReadyForPayment: true })
                    }>
                    Confirm and send for payment
                  </ExperienceButton>
                </div>
              )}
            </div>
          )
        }}
      </Formik>
      {errorMessage ? (
        <div
          className='experience-gp-modal-backdrop'
          role='alertdialog'
          aria-modal='true'
          aria-labelledby='gpSowErrorTitle'>
          <div className='experience-gp-pair-card experience-gp-error-card'>
            <div className='experience-gp-pair-head'>
              <h2 id='gpSowErrorTitle'>Unable to assign SOW</h2>
              <button
                type='button'
                className='experience-gp-x'
                aria-label='Close'
                onClick={() => setErrorMessage('')}>
                ×
              </button>
            </div>
            <BulkErrorMessage message={errorMessage} />
            <div className='experience-gp-modal-actions'>
              <ExperienceButton
                variant='primary'
                onClick={() => setErrorMessage('')}>
                OK
              </ExperienceButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

QuestAssignSOWModal.propTypes = {
  show: PropTypes.bool,
  pairs: PropTypes.arrayOf(
    PropTypes.shape({ clientId: PropTypes.string, coachId: PropTypes.string }),
  ),
  guides: PropTypes.arrayOf(PropTypes.object),
  schedulingMeta: PropTypes.shape({
    coachSchedulingFlagsByCoachId: PropTypes.object,
    forceManualScheduling: PropTypes.bool,
  }),
  currentQuest: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  initialValues: PropTypes.object.isRequired,
  onBackToPairing: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onAssigned: PropTypes.func.isRequired,
}

QuestAssignSOWModal.defaultProps = {
  show: false,
  pairs: [],
  guides: [],
  schedulingMeta: null,
  currentQuest: null,
}

export default QuestAssignSOWModal
